package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.react.{GenericState, GenericStateGenerator, RegisteredChangeAttribute, StateAttribute}
import com.neurobs.ccabserver.transcriptreview.util.{EditableTranscriptWord, RecordingTranscriptReviewHandler}
import com.neurobs.util.TimeSeconds
import org.scalajs.dom
import org.scalajs.dom.console
import slinky.core.{FunctionalComponent, TagElement}
import slinky.core.annotations.react
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.core.facade.React.createRef
import slinky.core.facade.{ReactElement, ReactRef}
import slinky.web.SyntheticMouseEvent

case class WordAdditionState
(
  recordingState: RecordingTranscriptReviewState,
  testState: TestTranscriptReviewState,
  boundingDivRef: ReactRef[dom.html.Div],
  initialX: StateAttribute[Option[Double]],
  cursorX: StateAttribute[Option[Double]],
  registeredChange: RegisteredChangeAttribute
) extends GenericState {

  import WordAdditionState._

	def recordingSilenceOffset: TimeSeconds = recordingState.handler.sourceData.audioSegment.interval.begin

  def dimensionAttributes = withTimes((startTime, endTime) => {
    val marginLeft = testState.secondsToPixels(startTime - recordingSilenceOffset)
    val width = testState.secondsToPixels(endTime - startTime)
    Seq[(String, scalajs.js.Any)](
      ("marginLeft", marginLeft + "px"),
      ("width", width + "px"),
      ("backgroundColor", "blue"),
      ("height", "20px")
    )
  })

  def registerChange(): Unit = registeredChange.register()

  def recordingReviewHandler = recordingState.handler

  def boundingLeft: Double = if (boundingDivRef.current == null) 0 else (boundingDivRef.current.getBoundingClientRect().left)

  def withXs[T](func: (Double, Double) => T): Option[T] = initialX.value.flatMap(x1 => cursorX.value.map(x2 => func(x1, x2)))
  def leftEdgeX: Option[Double] = withXs[Double](_.min(_))
  def rightEdgeX: Option[Double] = withXs[Double](_.max(_))

  def startTime: Option[TimeSeconds] = leftEdgeX.map(testState.pixelsToSeconds(_) + recordingSilenceOffset)
  def endTime: Option[TimeSeconds] = rightEdgeX.map(testState.pixelsToSeconds(_) + recordingSilenceOffset)
  def withTimes[T](func: (TimeSeconds, TimeSeconds) => T): Option[T] = startTime.flatMap(t1 => endTime.map(t2 => func(t1, t2)))

  def creatingWord: Boolean = initialX.value.isDefined

  def wordToAdd: Option[EditableTranscriptWord] = withTimes[EditableTranscriptWord](new EditableTranscriptWord(initialWord = "???", _, _))

  def handleMouseDown(event: SyntheticMouseEvent[TagElement#RefType]): Unit = {
    setOptionState(initialX, event.clientX - boundingLeft)
    registerChange()
  }

  def handleMouseMove(event: SyntheticMouseEvent[TagElement#RefType]): Unit = {
    if (initialX.value.isDefined) {
      setOptionState(cursorX, event.clientX - boundingLeft)
    }
    registerChange()
  }

  def handleMouseUp(event: SyntheticMouseEvent[TagElement#RefType]): Unit = {
    wordToAdd.map(newWord => {
      recordingReviewHandler.findOverlappingWord(newWord, MIN_OVERLAP_FRACTION) match {
        case Some(existingWord) =>
        case None =>
          recordingReviewHandler.addWord(newWord)
				testState.registerChange()
      }
    })
    voidState(initialX)
    voidState(cursorX)
    recordingState.registerChange()
  }
}

object WordAdditionState {
  val MIN_OVERLAP_FRACTION = .3
}

object WordAdditionStateGenerator extends GenericStateGenerator[WordAdditionState] {

  override def generateEmptyDefaultState(): WordAdditionState = {
    val temp = WordAdditionState(
		 recordingState = null,
		 testState = null,
		 boundingDivRef = createRef[dom.html.Div],
		 initialX = emptyOptionDefaultState[Double](),
		 cursorX = emptyOptionDefaultState[Double](),
		 registeredChange = emptyRegisteredChangeState()
    )
	  temp
  }
}

@react
object WordAdditionStateProvider {
  case class Props(children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

    val boundingDivRef = createRef[dom.html.Div]

    val recordingState = RecordingTranscriptReviewStateGenerator.accessState
    val testState = TestTranscriptReviewStateGenerator.accessState
    val initialXState = StateAttribute(useState[Option[Double]](None))
    val cursorXState = StateAttribute(useState[Option[Double]](None))
    val registeredChangeState = RegisteredChangeAttribute(useState(0))

    val wordAdditionState = WordAdditionState(
      recordingState,
      testState,
      boundingDivRef,
      initialXState,
      cursorXState,
      registeredChangeState
    )

    WordAdditionStateGenerator.Provider(wordAdditionState)(
      props.children:_*
    )
  }
}
